// Global
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// Components
import { ImageUpload } from '@/modules/Settings/components';
import { Input, TextEditorWithHtml } from '@/ui';
import { FormContentBlocksList } from '../formLists/FormContentBlocksList';
import { HintMessageWrapper, WideFormItem } from '../../UpdatePage.styles';

// Hooks
import { usePageValidations } from '../../hooks/usePageValidations';

// Store
import { selectCurrentPage } from '@/modules/Pages/feature';

// Types
import { VariableDetector } from 'src/shared/ui/VariablesDetector';
import { useVariableDetector } from 'src/shared/ui/VariablesDetector/hooks/useVariableDetector';
import { VariableType } from 'src/shared/ui/VariablesDetector/VariableDetector.typedefs';
import { UpdatePageInput } from '../../UpdatePage.typedefs';

// Helpers
import { imagesSizesConfig } from '../../../../utils/imagesSizesConfig';
import { getUpdatePageContentHintMessage } from '../../utils/getHintMessage';

export const UpdatePageContentFormPart = () => {
  const { t } = useTranslation(['common']);
  const currentPage = useSelector(selectCurrentPage);
  const { getRulesFor, getAcceptTypesFor, staticRules } = usePageValidations();
  const { variables, selectVariableInInput, registerInputRef, handleInputChange } = useVariableDetector();

  const contentProperties = currentPage?.schema?.properties?.content?.properties;

  if (!contentProperties) {
    return null;
  }

  const renderInputs = (contentSchema: any) => {
    return Object.keys(contentSchema).map((fieldName) => {
      const hintMessage = getUpdatePageContentHintMessage(currentPage.type, fieldName);
      switch (fieldName) {
        case UpdatePageInput.TEXT:
        case UpdatePageInput.TIPS:
        case UpdatePageInput.CALL:
        case UpdatePageInput.DESCRIPTION:
        case UpdatePageInput.NOT_FOUND_MESSAGE:
        case UpdatePageInput.NOT_FOUND_AND_CONTACT_SUPPORT_HINT:
          return (
            <WideFormItem
              key={fieldName}
              label={t(`update_page_form_content_${fieldName}`)}
              name={['content', fieldName]}
              rules={getRulesFor(['content', fieldName])}
              extra={
                !!hintMessage && (
                  // TODO: replace with backend value
                  <HintMessageWrapper>{t(hintMessage, { dynamicParam: 'try_again_btn' })}</HintMessageWrapper>
                )
              }
            >
              <TextEditorWithHtml rows={5} placeholder={t(`update_page_form_content_${fieldName}_placeholder`)} />
            </WideFormItem>
          );
        case UpdatePageInput.IMAGE:
          return (
            <WideFormItem
              label={t('update_page_form_content_image')}
              name={['content', 'image']}
              rules={getRulesFor(['content', 'image'])}
            >
              <ImageUpload
                accept={getAcceptTypesFor(['content', 'image'])}
                recommendedSize={imagesSizesConfig?.[currentPage.type]?.banner}
              />
            </WideFormItem>
          );
        case UpdatePageInput.IMAGE_NAME:
          return (
            <WideFormItem
              label={t('update_page_form_content_image_name')}
              name={['content', 'imageName']}
              rules={getRulesFor(['content', 'imageName'])}
            >
              <Input
                ref={(ref) => registerInputRef(t('update_page_form_content_image_name'), ref, VariableType.INPUT)}
                placeholder={t('update_page_form_content_image_name_placeholder')}
                onChange={(e) => handleInputChange(t('update_page_form_content_image_name'), e.target.value)}
              />
            </WideFormItem>
          );
        case UpdatePageInput.HEADER:
          return (
            <WideFormItem
              label={t('update_page_form_content_header_name')}
              name={['content', 'header']}
              rules={getRulesFor(['content', 'header'])}
            >
              <Input
                ref={(ref) => registerInputRef(t('update_page_form_content_header_name'), ref, VariableType.INPUT)}
                placeholder={t('update_page_form_content_header_name_placeholder')}
                onChange={(e) => handleInputChange(t('update_page_form_content_header_name'), e.target.value)}
              />
            </WideFormItem>
          );
        case UpdatePageInput.BLOCKS: {
          return (
            <FormContentBlocksList
              key={fieldName}
              fieldName={['content', 'blocks']}
              blockType={contentSchema?.blocks?.contentType}
              handleInputChange={handleInputChange}
              registerInputRef={registerInputRef}
            />
          );
        }
        default:
          return null;
      }
    });
  };

  return (
    <>
      <VariableDetector variables={variables} onSelectVariable={selectVariableInInput} />
      <WideFormItem label={t('page_title_label')} name={['content', 'title']} rules={staticRules.title}>
        <Input placeholder={t('page_title_placeholder')} />
      </WideFormItem>
      {renderInputs(contentProperties)}
    </>
  );
};
